import { useEffect, useRef, useState } from 'react';

interface TaskDateModalProps {
  /** Task name displayed in the modal header */
  taskName: string;
  /** Current start date (YYYY-MM-DD) */
  currentStartDate: string;
  /** Current end date (YYYY-MM-DD) */
  currentEndDate: string;
  /** Called with the number of business days to shift (positive = forward, negative = backward) */
  onConfirm: (daysDelta: number) => void;
  /** Called when the user cancels */
  onClose: () => void;
}

/**
 * Modal that lets keyboard users reschedule a task by entering a business-day shift.
 * Opened when a focused task bar receives Enter / Space.
 *
 * Uses a raw string for the input value so mid-type states like "-" are preserved
 * and not silently discarded by parseInt (which returns NaN for bare "-").
 */
export const TaskDateModal = ({
  taskName,
  currentStartDate,
  currentEndDate,
  onConfirm,
  onClose,
}: TaskDateModalProps) => {
  // Raw string so "-" is preserved while the user is mid-typing "-3"
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const titleId = 'task-date-modal-title';

  const parsedDelta = parseInt(inputValue, 10);
  const isValidDelta = !isNaN(parsedDelta) && parsedDelta !== 0;

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  // Escape closes the modal
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  const handleConfirm = () => {
    if (isValidDelta) {
      onConfirm(parsedDelta);
    }
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" role="presentation">
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className="bg-white rounded-lg shadow-xl p-6 w-full max-w-sm"
      >
        <h2 id={titleId} className="text-lg font-semibold text-gray-900 mb-1">
          Move Task
        </h2>
        <p className="text-sm text-gray-600 mb-4 truncate" title={taskName}>
          {taskName}
        </p>

        <div className="text-sm text-gray-600 mb-4">
          <div>Current: {currentStartDate} → {currentEndDate}</div>
        </div>

        <label htmlFor="days-delta" className="block text-sm font-medium text-gray-700 mb-1">
          Shift by business days (negative = earlier)
        </label>
        <input
          id="days-delta"
          ref={inputRef}
          type="text"
          inputMode="numeric"
          pattern="-?[0-9]*"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              handleConfirm();
            }
          }}
          className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
          aria-invalid={inputValue !== '' && !isValidDelta}
        />

        <div className="flex justify-end gap-3 mt-6">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            disabled={!isValidDelta}
            className="px-4 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-md hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Move Task
          </button>
        </div>
      </div>
    </div>
  );
};
